/**
 * Bingo Card Component
 *
 * Displays a bingo card grid with cells and highlights completed patterns.
 */

import { useMemo } from 'react';
import type { BingoCard, BingoCell, BingoPattern } from '@/types/bingo';
import { BingoCellComponent } from './bingo-cell';
import clsx from 'clsx';

interface BingoCardProps {
  card: BingoCard;
  cells: BingoCell[];
  patterns?: BingoPattern[];
  onCellClick?: (cell: BingoCell) => void;
  onCellComplete?: (cell: BingoCell) => void;
  size?: 'sm' | 'md' | 'lg';
}

export function BingoCardComponent({
  card,
  cells,
  patterns = [],
  onCellClick,
  onCellComplete,
  size = 'md',
}: BingoCardProps) {
  // Build grid map for efficient lookup
  const cellMap = useMemo(() => {
    const map = new Map<string, BingoCell>();
    cells.forEach((cell) => {
      map.set(`${cell.row}-${cell.column}`, cell);
    });
    return map;
  }, [cells]);

  // Determine which cells are part of completed patterns
  const patternCells = useMemo(() => {
    const patternSet = new Set<string>();
    patterns.forEach((pattern) => {
      pattern.goalsInPattern.forEach((cellId) => {
        patternSet.add(cellId);
      });
    });
    return patternSet;
  }, [patterns]);

  // Grid size classes
  const gridClasses = {
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
    7: 'grid-cols-7',
  };

  return (
    <div className="w-full">
      {/* Card Container with Glass Morphism Effect */}
      <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-white/50">
        {/* Card header */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent">
              {card.title}
            </h1>
            <span className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full text-xs font-semibold text-purple-700">
              {card.year}
            </span>
          </div>
          <p className="text-gray-600 text-sm">
            Complete rows, columns, or diagonals to win! 🎯
          </p>
        </div>

        {/* Bingo grid */}
        <div
          className={clsx(
            'grid',
            gridClasses[card.gridSize as keyof typeof gridClasses],
            'gap-2',
            'w-full',
            'mb-4',
            'auto-rows-fr'
          )}
          style={{
            maxHeight: 'calc(100vh - 350px)',
            aspectRatio: '1 / 1'
          }}
        >
          {Array.from({ length: card.gridSize * card.gridSize }, (_, index) => {
            const row = Math.floor(index / card.gridSize);
            const col = index % card.gridSize;
            const cell = cellMap.get(`${row}-${col}`);
            const cellKey = `${row}-${col}`;

            if (!cell) {
              // Empty cell with more inviting design
              return (
                <div
                  key={cellKey}
                  className={clsx(
                    'border-2 border-dashed border-purple-200 rounded-lg',
                    'bg-gradient-to-br from-purple-50/50 to-pink-50/50',
                    'hover:from-purple-100/50 hover:to-pink-100/50',
                    'transition-all duration-300 ease-in-out',
                    'hover:border-purple-300 hover:scale-[1.02]',
                    'p-2',
                    'w-full h-full min-h-[80px]',
                    'flex flex-col items-center justify-center',
                    'cursor-pointer group'
                  )}
                  aria-label={`Empty cell at row ${row + 1}, column ${col + 1}`}
                >
                  <div className="text-2xl opacity-30 group-hover:opacity-50 transition-opacity">
                    ✨
                  </div>
                  <p className="text-xs text-gray-400 mt-1 text-center opacity-0 group-hover:opacity-70 transition-opacity">
                    Add a goal
                  </p>
                </div>
              );
            }

            const isInPattern = patternCells.has(cell.id);

            return (
              <BingoCellComponent
                key={cell.id}
                cell={cell}
                onClick={onCellClick}
                onComplete={onCellComplete}
                size={size}
                showPattern={isInPattern}
              />
            );
          })}
        </div>

        {/* Pattern indicators with badges */}
        {patterns.length > 0 && (
          <div className="mt-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">🎉</span>
              <p className="font-bold text-yellow-900 text-sm">Completed Patterns!</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {patterns.map((pattern) => (
                <span
                  key={pattern.id}
                  className="px-2.5 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full text-xs font-semibold shadow-md"
                >
                  {pattern.patternType === 'row' && `🎯 Row ${pattern.patternIndex + 1}`}
                  {pattern.patternType === 'column' && `📍 Column ${pattern.patternIndex + 1}`}
                  {pattern.patternType === 'diagonal_main' && '✨ Main Diagonal'}
                  {pattern.patternType === 'diagonal_anti' && '⭐ Anti-Diagonal'}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

